/**
 * Same-origin admin portal paths open in the current tab (React Router).
 * External URLs (Jira, utilities, etc.) open in a new tab.
 * Unsafe schemes (javascript:, data:, etc.) are rejected — never passed to <a href>.
 */
export type ChatLinkTarget =
  | { kind: 'internal'; to: string }
  | { kind: 'external'; href: string }

const SAFE_EXTERNAL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])

function isSafeExternalUrl(url: URL): boolean {
  return SAFE_EXTERNAL_PROTOCOLS.has(url.protocol.toLowerCase())
}

export function resolveChatLink(
  href: string | undefined | null,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): ChatLinkTarget | null {
  if (!href) return null
  const trimmed = href.trim()
  if (!trimmed) return null

  // App-relative path (not protocol-relative //host)
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return { kind: 'internal', to: trimmed }
  }

  try {
    const url = new URL(trimmed, origin || 'http://localhost')
    if (origin && url.origin === origin) {
      return { kind: 'internal', to: `${url.pathname}${url.search}${url.hash}` }
    }
    if (!isSafeExternalUrl(url)) return null
    return { kind: 'external', href: url.href }
  } catch {
    return null
  }
}

/** react-markdown urlTransform: drop javascript:/data:/etc. before they reach <a>. */
export function transformChatMarkdownUrl(url: string): string {
  const target = resolveChatLink(url)
  if (!target) return ''
  return target.kind === 'internal' ? target.to : target.href
}
